/**
 * KpiWithDelta — KPI tile primitive.
 *
 * Renders inside a WidgetCard (or standalone). Shows:
 *   - Headline value (big number)
 *   - Optional unit suffix
 *   - Delta vs previous period (+/- absolute or % with up/down arrow)
 *   - Optional inline sparkline (pure SVG; no chart library dependency — looks
 *     identical regardless of the active slicer library)
 *
 * Delta semantics: pass `goodWhen='up'` (default) when increases are positive
 * (e.g., uptime, active users) — green = up. Pass `goodWhen='down'` for
 * metrics where decreases are positive (e.g., denied packets, error rate).
 */

import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { ArrowUp, ArrowDown, Minus } from '@/utils/icons';

interface KpiWithDeltaProps {
  /** Headline value — numeric or pre-formatted string. */
  value: number | string;
  /** Optional unit (e.g., "%", " ms", " users"). Appended to the value. */
  unit?: string;
  /** Previous-period numeric value used to compute the delta. Pass null to hide delta. */
  previousValue?: number | null;
  /** Display delta as percentage (default) or absolute change. */
  deltaMode?: 'percent' | 'absolute';
  /** Direction that should be styled green. Default 'up'. */
  goodWhen?: 'up' | 'down';
  /** Time-series for inline sparkline. Omit to hide sparkline. */
  series?: number[];
  /** Optional subtitle below the headline. */
  subtitle?: string;
  className?: string;
}

function formatDelta(current: number, previous: number, mode: 'percent' | 'absolute'): {
  text: string;
  direction: 'up' | 'down' | 'flat';
} {
  const diff = current - previous;
  const direction = diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat';
  if (mode === 'absolute') {
    return { text: diff === 0 ? '0' : `${diff > 0 ? '+' : ''}${diff.toLocaleString()}`, direction };
  }
  if (previous === 0) {
    return { text: diff === 0 ? '0%' : '∞', direction };
  }
  const pct = (diff / Math.abs(previous)) * 100;
  return {
    text: `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`,
    direction,
  };
}

function Sparkline({ data }: { data: number[] }) {
  const { path, width, height } = useMemo(() => {
    const w = 80;
    const h = 24;
    if (data.length === 0) return { path: '', width: w, height: h };
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const stepX = data.length > 1 ? w / (data.length - 1) : 0;
    const d = data
      .map((v, i) => {
        const x = i * stepX;
        const y = h - ((v - min) / range) * h;
        return `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
      })
      .join(' ');
    return { path: d, width: w, height: h };
  }, [data]);

  if (!path) return null;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="dls-text-neeve-primary"
      aria-hidden
    >
      <path d={path} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function KpiWithDelta({
  value,
  unit,
  previousValue,
  deltaMode = 'percent',
  goodWhen = 'up',
  series,
  subtitle,
  className,
}: KpiWithDeltaProps) {
  const showDelta = typeof previousValue === 'number' && typeof value === 'number';
  const delta = showDelta ? formatDelta(value as number, previousValue as number, deltaMode) : null;

  let deltaClass = 'dls-text-muted-foreground';
  if (delta) {
    if (delta.direction === 'flat') {
      deltaClass = 'dls-text-muted-foreground';
    } else if (
      (delta.direction === 'up' && goodWhen === 'up') ||
      (delta.direction === 'down' && goodWhen === 'down')
    ) {
      deltaClass = 'dls-text-status-online';
    } else {
      deltaClass = 'dls-text-status-offline';
    }
  }

  return (
    <div className={cn('dls-flex dls-flex-col dls-gap-1', className)}>
      <div className="dls-flex dls-items-end dls-justify-between dls-gap-3">
        <div className="dls-flex dls-items-baseline dls-gap-1">
          <span className="dls-text-4xl dls-font-bold dls-leading-none dls-text-foreground">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </span>
          {unit && <span className="dls-text-sm dls-text-muted-foreground">{unit}</span>}
        </div>
        {series && series.length > 0 && <Sparkline data={series} />}
      </div>
      {delta && (
        <div className={cn('dls-flex dls-items-center dls-gap-1 dls-text-xs dls-font-medium', deltaClass)}>
          {delta.direction === 'up' && <ArrowUp className="dls-h-3 dls-w-3" aria-hidden />}
          {delta.direction === 'down' && <ArrowDown className="dls-h-3 dls-w-3" aria-hidden />}
          {delta.direction === 'flat' && <Minus className="dls-h-3 dls-w-3" aria-hidden />}
          <span>{delta.text}</span>
          <span className="dls-text-muted-foreground dls-font-normal">vs previous</span>
        </div>
      )}
      {subtitle && <p className="dls-text-xs dls-text-muted-foreground">{subtitle}</p>}
    </div>
  );
}
